import {BedConfiguration, Bedroom, ChildPolicy} from '@findhotel/sapi'
import {propOr, uniqBy} from 'ramda'

import {RoomSplitType} from '@vio/core/src/room/types/RoomConfiguration'

import type {Room} from '@vio/core/src/room/types/room'

export type BedConfigurationOption = {
  items: BedConfiguration[][]
  hash: string
}

/**
 * Temporary implementation of collectBy from Ramda.
 * TODO: Remove this when Ramda is upgraded to a version that includes collectBy.
 */
const collectBy = <T, K extends PropertyKey>(
  fn: (item: T) => K,
  list: T[]
): T[][] => {
  const groups: Record<K, T[]> = {} as Record<K, T[]>
  list.forEach(item => {
    const key = fn(item)
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(item)
  })
  return Object.values(groups)
}

export const cartesian = <T>(arr: T[][]): T[][] =>
  arr.reduce<T[][]>(
    (acc, curr) =>
      acc.map(a => curr.map(c => [...a, c])).reduce((a, b) => a.concat(b), []),
    [[]]
  )

export const groupBedTypesByConfig = (bedTypes: BedConfiguration[]) => {
  if (!bedTypes) return []
  return collectBy(propOr('', 'cfgID'), bedTypes)
}

/**
 * Bed configurations without a typeID carry no usable bed-type data (see
 * sc-190960), so they're dropped before bedrooms are grouped into options.
 * A bedroom left with no valid configurations is excluded from the
 * cartesian product entirely, rather than collapsing all options to none.
 */
export const mapBedroomsToUniqueBedConfigurationOptions = (
  bedrooms: Bedroom[]
): BedConfigurationOption[] => {
  const grouppedBedroomConfigurations = bedrooms
    .map(bedroom =>
      groupBedTypesByConfig(
        (bedroom.bedConfigurations || []).filter(({typeID}) => Boolean(typeID))
      )
    )
    .filter(groups => groups.length > 0)

  if (!grouppedBedroomConfigurations.length) return []

  const possibleOptions = cartesian(grouppedBedroomConfigurations).map(
    combination => ({
      items: combination,
      hash: bedConfigHash(combination)
    })
  )

  return uniqBy(option => option.hash, possibleOptions)
}

export const bedConfigHash = (bedConfigs: BedConfiguration[][]): string =>
  bedConfigs.reduce(
    (acc, curr) =>
      acc +
      (acc ? '; ' : '') +
      curr.map(c => `${c.count}×${c.typeID}`).join(','),
    ''
  )

/**
 * Count children under the age of 3 across all split rooms.
 */
export const getChildrenUnder3Count = (roomsSplit: RoomSplitType[]) =>
  roomsSplit.reduce(
    (acc, room) => acc + room.children.filter(age => age <= 3).length,
    0
  )

/**
 * Crib availability requires both conditions:
 * 1) Search criteria includes more than one child aged ≤ 3.
 * 2) allowCribs is true in the room configuration.
 */
export const isCribAvailable = (
  roomsSplit: RoomSplitType[],
  allowCribs: boolean
) => {
  const childrenUnder3Count = getChildrenUnder3Count(roomsSplit)
  return Boolean(childrenUnder3Count) && allowCribs
}

/**
 * Determines if free child stay is available based on the provided child policy and guest room split.
 *
 * @param roomsSplit - Array describing the split rooms and the ages of children in each room.
 * @param childPolicy - Child policy object containing maxAge and free flags.
 * @returns True if free child stay is available for all children in all rooms, false otherwise.
 */
export const isFreeChildStay = (
  roomsSplit: RoomSplitType[],
  {maxAge = 0, free = false}: ChildPolicy = {}
) => {
  const childrenAges = getFlattenedChildrenAges(roomsSplit)
  return (
    free && childrenAges.length > 0 && childrenAges.every(age => age <= maxAge)
  )
}

/**
 * Get total adults across all split rooms.
 */
export const getTotalAdults = (roomsSplit: RoomSplitType[]) =>
  roomsSplit.reduce((sum, room) => sum + room.adults, 0)

/**
 * Get total children across all split rooms.
 */
export const getTotalChildren = (roomsSplit: RoomSplitType[]) =>
  roomsSplit.reduce((sum, room) => sum + room.children.length, 0)

/**
 * Flattens and returns an array of all children ages from split rooms.
 *
 * @param roomsSplit - Array of split rooms containing children age data.
 * @returns A flat array of ages of all children across all rooms.
 */
export const getFlattenedChildrenAges = (roomsSplit: RoomSplitType[]) =>
  roomsSplit.reduce<number[]>((acc, room) => {
    return [...acc, ...room.children]
  }, [])

/**
 * Check if any room has at least 2 adults with a child older than 3 years old (\> 3).
 */
export const hasAdultsWithChildOver3 = (roomsSplit: RoomSplitType[]) =>
  roomsSplit.some(
    room => room.adults >= 2 && room.children.some(age => age > 3)
  )

/**
 * Extra bed availability is based on these conditions:
 * 1) The search criteria includes more than 2 adults OR at least 2 adults with a child older than 3.
 * 2) allowExtraBeds is true for this room.
 */
export const areExtraBedsAvailable = (
  roomsSplit: RoomSplitType[],
  allowExtraBeds: boolean
) => {
  const totalAdults = getTotalAdults(roomsSplit)
  return (
    allowExtraBeds && (totalAdults > 2 || hasAdultsWithChildOver3(roomsSplit))
  )
}

/**
 * Get the number of sleeps for the room.
 *
 * @param roomsSplit - The room split configuration from user's search criteria.
 *   - totalAdults: Number of adults chosen by the user in the search criteria.
 *   - totalChildren: Number of children chosen by the user in the search criteria.
 * @param room - The room data containing occupancy information from the API response.
 *   - maxAdults: Maximum number of adults the room can accommodate.
 *   - maxOccupancy: Maximum total occupancy (adults + children) the room can accommodate.
 *
 * @returns maxAdults if searching for adults only, maxOccupancy if searching with children,
 *          or null if no valid search criteria.
 */
export const getSleepsNumber = ({
  roomsSplit,
  room
}: {
  roomsSplit: RoomSplitType[]
  room: Pick<Room, 'occupancy'>
}) => {
  const {maxAdults, maxOccupancy} = room.occupancy || {}
  if (!roomsSplit?.length) return null

  const totalAdults = getTotalAdults(roomsSplit)
  const totalChildren = getTotalChildren(roomsSplit)
  if (totalAdults > 0 && totalChildren === 0) return maxAdults
  if (totalAdults > 0 && totalChildren > 0) return maxOccupancy
  return null
}
